import useProfile from "@/hooks/useProfile";
import { getMembersQueryOptions } from "@/queries";
import type { ContactMember } from "@/types";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import compact from "lodash/compact";
import isBoolean from "lodash/isBoolean";
import { useEffect, useMemo, useRef } from "react";
import type { SetOptional } from "type-fest";

interface UseContactPeoplesBaseProps {
	search: string | null;
	isDirectory: boolean | null;
	excludeProfile?: boolean;
	estimateSize?: number;
	gap?: number;
	paddingStart?: number;
	paddingEnd?: number;
}

type UseContactPeoplesProps = SetOptional<UseContactPeoplesBaseProps, "search">;

function useContactPeoples(props: UseContactPeoplesProps) {
	const { estimateSize = 95, gap = 16, paddingEnd, paddingStart } = props;
	const scrollRef = useRef<HTMLDivElement>(null);
	const { profile } = useProfile();
	const {
		data: resPeoples,
		fetchNextPage,
		isError: isPeoplesError,
		isFetching: isFetchingPeoples,
		isLoading: isLoadingPeoples,
		isFetchingNextPage,
		hasNextPage,
	} = useSuspenseInfiniteQuery(
		getMembersQueryOptions({
			...(isBoolean(props.isDirectory) ? { directory: props.isDirectory } : {}),
		}),
	);

	const peoples = useMemo(() => {
		const peoples = resPeoples.filter((item) => item.id !== profile?.id);
		const search = props.search;
		if (search) {
			const items = peoples.filter(
				(item) =>
					item.fullname?.toLowerCase()?.includes(search.toLowerCase()) ||
					item.hospital?.name?.toLowerCase()?.includes(search.toLowerCase()) ||
					item.job_title?.toLowerCase()?.includes(search.toLowerCase()),
			);
			return compact(items) as ContactMember[];
		}
		if (props.excludeProfile) return compact(peoples);
		return compact([profile, ...peoples]) as ContactMember[];
	}, [resPeoples, props.search, props.excludeProfile, profile]);

	const isLoading = useMemo(
		() => isLoadingPeoples || isFetchingPeoples || isFetchingNextPage,
		[isLoadingPeoples, isFetchingPeoples, isFetchingNextPage],
	);

	const isEmpty = useMemo(
		() => !isLoading && !props.search && !peoples.length,
		[isLoading, peoples, props.search],
	);

	const isEmptySearch = useMemo(
		() => !isLoading && !!props.search && !peoples.length,
		[isLoading, peoples, props.search],
	);

	const memberVirtualizer = useVirtualizer({
		count: peoples.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => estimateSize,
		measureElement: (el) => el.getBoundingClientRect().height,
		paddingStart,
		paddingEnd: peoples.length ? paddingEnd : undefined,
		overscan: 5,
		gap,
	});

	const memberVirtualizerItems = memberVirtualizer.getVirtualItems();
	useEffect(() => {
		const [lastItem] = [...memberVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= peoples.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		hasNextPage,
		fetchNextPage,
		peoples.length,
		isFetchingNextPage,
		memberVirtualizerItems,
	]);

	return {
		data: resPeoples,
		scrollRef,
		profile,
		peoples,
		isError: isPeoplesError,
		isLoading,
		isEmpty,
		isEmptySearch,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		virtualizer: {
			...memberVirtualizer,
			items: memberVirtualizerItems,
		},
	};
}

export default useContactPeoples;
